import { useAtomValue } from 'jotai'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'react-toastify'
import { match, P } from 'ts-pattern'
import type { Promisable } from 'type-fest'
import FluentThumbDislike20Regular from '~icons/fluent/thumb-dislike-20-regular'
import IcBaselineHideImage from '~icons/ic/baseline-hide-image'
import IcBaselineUndo from '~icons/ic/baseline-undo'
import IcOutlineSms from '~icons/ic/outline-sms'
import ArtworkLikeButton from 'src/components/Artwork/ArtworkLikeButton'
import { createTagPageUrl } from 'src/components/Tag/utils'
import { UIMenuSeparatorSymbol } from 'src/components/ui/Menu'
import Text from 'src/components/ui/Text'
import { executeMarkArtwork } from 'src/services/apollo/artwork'
import type {
  ArtworkPreviewFragment,
  ArtworkWithMangaPreviewFragment,
  TackPreviewFragment,
} from 'src/services/apollo/generated/graphql'
import {
  ArtworkType,
  ArtworkVisibilityType,
  RefType,
} from 'src/services/apollo/generated/graphql'
import { useBlurNsfw } from 'src/services/helpers/preference'
import { useTacksByIds } from 'src/services/orpc/tack'
import type { BatchTackItem } from 'src/services/orpc/tackBatcher'
import { localeAtom } from 'src/stores/locale'
import { getArtworkLink, shouldArtworkBeBlurred } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { useArtworkGridCols } from 'src/utils/hooks/useArtworkGridCols'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { useLocation } from 'src/utils/router/route'
import { track, trackImageForRec } from 'src/utils/tracker'
import { getUserDisplayName } from 'src/utils/user'
import { MarkType } from '../../services/apollo/generated/graphql'
import { CardCornerDebugInfo } from '../Admin/UserContentInfo/CardCornerDebugInfo'
import { CardCornerOverlay } from '../CardCornerOverlay'
import { UIButton } from '../ui/Button'
import { ArtworkPrivateLabel } from './ArtworkPrivateLabel'
import ArtworkTypeLabel from './ArtworkTypeLabel'
import type { BaseArtworkItemPreviewCardProps } from './BaseArtworkItemPreviewCard'
import BaseArtworkItemPreviewCard from './BaseArtworkItemPreviewCard'

export type ArtworkPreviewCardSimpleProps = BaseArtworkItemPreviewCardProps & {
  /**
   * Accepts the GraphQL `ArtworkPreviewFragment` shape, optionally extended
   * with oRPC-only fields set by the list-feed adapter:
   * - `tackIds`: when `tacks` is empty, the card hydrates tags via the
   *   dedup'd /v2/tack/batch loader.
   * - `debugInfo`: admin-only overlay (paidCredit / aesScore / safety scores)
   *   surfaced on the corner debug HUD.
   */
  artwork?: ArtworkPreviewFragment & {
    tackIds?: string[]
    debugInfo?: {
      paidCredit?: number | null
      aesScore?: number | null
      textSafetyScore?: number | null
      mediaSafetyScore?: number | null
    } | null
  }
  manga?: ArtworkWithMangaPreviewFragment['manga']
  showAuthor?: boolean
  loading?: boolean
  onArtworkClick?: (artwork?: ArtworkPreviewFragment, index?: number) => void

  /**
   * Extra tracking handler
   * For `Rec` related tracking events
   */
  onLikeClickForRec?: (artwork?: ArtworkPreviewFragment) => void

  showDislike?: boolean
  showDislikeOverlay?: boolean
  contentSafetyLabelType?: 'sensitive' | 'flagged'

  /** When false, hides mediaExtra overlays (comment count, artwork type label, content safety label) */
  showOverlayInfo?: boolean

  /** For ArtworkLikeButton */
  oauthReturnOpensDetail?: boolean
}

const ArtworkPreviewCardSimple = memo(
  ({
    artwork,
    manga,
    showAuthor,
    loading,
    showLikeBtn = true,
    trackingArgs,
    showIndex,
    index,
    onArtworkClick,
    onLikeClickForRec,
    showDislike,
    showDislikeOverlay,
    contentSafetyLabelType,
    showOverlayInfo = true,
    oauthReturnOpensDetail,
    ...props
  }: ArtworkPreviewCardSimpleProps) => {
    const locale = useAtomValue(localeAtom)
    const totalCols = useArtworkGridCols()
    const { title, prompts, createdAt } = artwork || {}
    const cardLabel = title || prompts

    const [blurNsfw] = useBlurNsfw()
    const authorName = getUserDisplayName(artwork?.author)
    const isMobile = useIsMobile()

    // GraphQL feeds populate `artwork.tacks` directly. The oRPC recommend feed
    // only carries `tackIds`; hydrate them via the dedup'd batch loader. Each
    // card calls `useTacksByIds` for its own ids; concurrent cards across the
    // page coalesce into a single /v2/tack/batch RPC per microtask.
    const orpcTackIds = artwork?.tackIds ?? []
    const needsHydration =
      orpcTackIds.length > 0 && (artwork?.tacks?.length ?? 0) === 0
    // localeAtom only stores resolved locales (already base codes), so we
    // can pass it straight through.
    const hydratedTackMap = useTacksByIds(
      needsHydration ? orpcTackIds : [],
      locale,
    )
    const hydratedTacks: TackPreviewFragment[] = needsHydration
      ? orpcTackIds
          .map(id => hydratedTackMap.get(id))
          .filter((t): t is BatchTackItem => t != null)
          .map(toTackPreviewFragment)
      : []
    const sourceTacks =
      (artwork?.tacks?.length ?? 0) > 0 ? artwork!.tacks! : hydratedTacks
    const tacksToShow = sourceTacks.slice(0, isMobile ? 1 : 3)

    const blurForPreview = shouldArtworkBeBlurred(artwork) && blurNsfw

    const location = useLocation()

    const [isDisliked, setIsDisliked] = useState(false)
    const toggleDislike = async (target: boolean) => {
      const eventName = target ? 'dislike_click' : 'undo_dislike_click'
      trackImageForRec(eventName, {
        feed: trackingArgs?.feed || 'trending',
        totalCols,
        scene: 'artwork_feed',
        likeFrom: 'grid',
        artworkId: artwork?.id ?? '',
        index: index ?? 0,
        currentLikeCount: artwork?.likedCount ?? 0,
        type: RefType.Artwork,
        retrieverId: artwork?.feedMetadata?.retrieverId,
        pathname: location.pathname,
      })
      if (!artwork) return
      try {
        await executeMarkArtwork({
          artworkId: artwork.id,
          type: MarkType.Dislike,
          target,
        })
        if (target) {
          toast.success(<Text i18nKey="action:dislike.success" />)
        } else {
          toast.success(<Text i18nKey="action:dislike.revert.success" />)
        }
        setIsDisliked(target)
      } catch (err) {
        if (err instanceof Error) {
          toast.error(err.message)
        }
        console.error(err)
      }
    }
    const handleDislike = () => toggleDislike(true)
    const handleUndoDislike = () => toggleDislike(false)

    const canShowDislikeOverlay =
      showDislike && showDislikeOverlay && isDisliked

    return (
      <BaseArtworkItemPreviewCard
        mediaId={
          artwork?.mediaId ||
          manga?.coverMediaId ||
          manga?.chapter?.coverMediaId
        }
        media={artwork?.media}
        nsfwBlur={blurForPreview}
        showExplicitIcon={blurForPreview}
        label={<span title={cardLabel}>{cardLabel}</span>}
        secondLabel={showAuthor && authorName}
        time={createdAt}
        artwork={artwork}
        showLikeBtn={showLikeBtn}
        loading={loading}
        trackingArgs={trackingArgs}
        mediaExtra={
          showOverlayInfo ? (
            <>
              {/* Artwork type label */}
              {artwork && (
                <CardCornerOverlay
                  corner="top-left"
                  className="bg-transparent shadow-none backdrop-filter-none"
                >
                  <ArtworkTypeLabel
                    artwork={artwork}
                    className="absolute top-1 left-1 ring-1 ring-black/20"
                  />
                </CardCornerOverlay>
              )}
              {/* Tags overlay at bottom of image (controlled by experiment: anonymous-only or all users).
                  When tags are shown, comment count is absorbed into the same row to avoid overlap. */}
              {match({ artwork, tacksToShow })
                .with(
                  { tacksToShow: P.when(t => t.length > 0) },
                  () => null, // rendered outside the card anchor via mediaExtraLinks (main #11xxx)
                )
                .with(
                  { artwork: { visibility: ArtworkVisibilityType.Private } },
                  () => (
                    <CardCornerOverlay
                      corner="bottom-left"
                      className="bg-transparent shadow-none backdrop-filter-none"
                    >
                      <ArtworkPrivateLabel />
                    </CardCornerOverlay>
                  ),
                )
                .with(
                  { artwork: { commentCount: P.number.gt(0) } },
                  ({ artwork: aw }) => (
                    <CardCornerOverlay corner="bottom-left">
                      <div className="flex items-center gap-1">
                        <IcOutlineSms className="text-white/70 text-xl" />
                        <span className="text-white/90">
                          {aw!.commentCount}
                        </span>
                      </div>
                    </CardCornerOverlay>
                  ),
                )
                .otherwise(() => null)}
              {contentSafetyLabelType && (
                <ContentSafetyLabel type={contentSafetyLabelType} />
              )}
              <CardCornerDebugInfo
                extra={artwork?.extra}
                debugInfo={artwork?.debugInfo}
                feedMetadata={artwork?.feedMetadata}
                paidCredit={artwork?.paidCredit}
                aesScore={artwork?.aesScore}
              >
                {content => (
                  // Note: This label will cover Artwork's menu button, and this is intended. (From Zeno)
                  <CardCornerOverlay corner="top-right">
                    {content}
                  </CardCornerOverlay>
                )}
              </CardCornerDebugInfo>
            </>
          ) : null
        }
        likeBtn={
          artwork && (
            <ArtworkLikeButton
              artwork={artwork}
              artworkId={artwork.id}
              initialCount={artwork.likedCount}
              initialValue={artwork.liked}
              afterClick={onLikeClickForRec}
              oauthReturnOpensDetail={oauthReturnOpensDetail}
              hideValueOnZero
              trackingArgs={trackingArgs}
            />
          )
        }
        showAuthorAvatar
        contentClassName="group"
        border={'author'}
        contentExtra={
          showIndex && index != null ? (
            <p
              className={cx(
                'absolute top-2 right-2 grid place-content-center text-white font-black font-quicksand text-xl rounded-full shadow-md tabular-nums size-10 pointer-events-none',
                match(index + 1)
                  .with(1, () => 'bg-amber-400')
                  .with(2, () => 'bg-slate-400')
                  .with(3, () => 'bg-yellow-700')
                  .otherwise(() => 'bg-purple-700 bg-opacity-80'),
              )}
            >
              {index + 1}
            </p>
          ) : undefined
        }
        mediaExtraLinks={
          showOverlayInfo && tacksToShow.length > 0 ? (
            <div
              className="absolute bottom-0 left-0 right-0 flex gap-1 px-2 pb-2 pt-6 pr-14 md:pr-2 flex-wrap pointer-events-none"
              style={{
                background:
                  'linear-gradient(to top, rgba(0,0,0,0.45), transparent)',
              }}
            >
              {artwork?.commentCount != null && artwork.commentCount > 0 && (
                <span className="text-white text-xs px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm flex items-center gap-1">
                  <IcOutlineSms className="text-white/70 text-sm" />
                  {artwork.commentCount}
                </span>
              )}
              {tacksToShow.map(tack => (
                <Link
                  key={tack.id}
                  to={createTagPageUrl('artwork', tack.codeName)}
                  className="text-white text-xs px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 pointer-events-auto truncate max-w-full"
                  onClick={e => e.stopPropagation()}
                >
                  #{tack.displayName}
                </Link>
              ))}
            </div>
          ) : null
        }
        overlay={
          canShowDislikeOverlay ? (
            <DislikeOverlay onUndoDislike={handleUndoDislike} />
          ) : null
        }
        {...props}
        menu={[
          showDislike && {
            id: 'dislike',
            icon: <FluentThumbDislike20Regular className="size-5" />,
            children: <Text i18nKey="action:dislike.label" />,
            onAction: handleDislike,
          },
          showDislike && UIMenuSeparatorSymbol,
          ...(props.menu ?? []),
        ]}
        onClick={(e, ...args) => {
          if (artwork?.type === ArtworkType.AnimatedComic) {
            track('animated_comic_click')
            e.preventDefault()
            if (manga) return window.open(getArtworkLink(artwork), '_blank')
          }
          props.onClick?.(e, ...args)
          onArtworkClick?.(artwork)
        }}
      />
    )
  },
)

ArtworkPreviewCardSimple.displayName = 'ArtworkPreviewCardSimple'

export default ArtworkPreviewCardSimple

/**
 * Synthesize a TackPreviewFragment from a /v2/tack/batch item. We surface the
 * server-resolved `displayName` via `defaultName` and omit `tackTerms`, so the
 * card's `getLocalizedTackName(undefined, displayName, locale)` falls through
 * to `defaultName` and returns the localized name unchanged.
 */
function toTackPreviewFragment(item: BatchTackItem): TackPreviewFragment {
  return {
    __typename: 'Tack',
    id: item.id,
    codeName: item.codeName,
    defaultName: item.displayName,
    displayName: item.displayName,
  }
}

function ContentSafetyLabel(props: { type: 'sensitive' | 'flagged' }) {
  const { t } = useTranslation(['artwork'])
  const { type } = props
  return (
    <div className="absolute py-1 px-2 text-xs z-10 backdrop-blur backdrop-brightness-50 shadow-lg bottom-9 left-0 rounded-r-md md:rounded-tl-lg md:rounded-r-none md:py-2 md:right-0 md:left-[unset] md:bottom-0 md:text-base">
      <p
        className={cx(
          type === 'sensitive'
            ? 'text-rose-400 font-bold'
            : 'text-orange-200 font-medium',
        )}
      >
        {type === 'sensitive'
          ? t('artwork:content-safety.sensitive.label')
          : t('artwork:content-safety.flagged.label')}
      </p>
    </div>
  )
}

interface DislikeOverlayProps {
  onUndoDislike?: () => Promisable<void>
}

function DislikeOverlay(props: DislikeOverlayProps) {
  const { onUndoDislike } = props

  return (
    <div className="rounded-md size-full bg-card-background/65 backdrop-blur-xl flex flex-col items-center justify-center gap-3 *:opacity-50">
      <IcBaselineHideImage className="size-10 md:size-16" />
      <span className="font-bold text-center">
        <Text i18nKey="action:dislike.success" />
      </span>
      <span className="text-xs px-4 md:px-6">
        <Text i18nKey="action:dislike.description" />
      </span>
      <UIButton
        size="small"
        className="text-xs"
        onPress={onUndoDislike}
        variant="outlined"
        busy="auto"
        icon={<IcBaselineUndo className="size-3" />}
      >
        <Text i18nKey="action:dislike.revert.label" />
      </UIButton>
    </div>
  )
}
